/**
 * MA-ARCH-205: CompactFunnelPreview — Phase 4
 *
 * Renders a live preview of how the funnel will appear in compact mode.
 * Reads theme from builder_data.theme at render time (no copied theme).
 *
 * Architecture proof for Phase 5:
 *   - This is a native React component (no iframe, no overlay, no modal)
 *   - It renders inline within its parent container
 *   - It reads builder_data.theme via props → brandConfig → resolveThemeTokens
 *   - When embedded in the LP (Phase 5), it will share the same theme tokens
 *     and render as a native page section above the fold
 *
 * Props:
 *   steps       — builder_data.funnel.steps (5 generated steps)
 *   theme       — builder_data.theme ({theme_id, colors, border_radius})
 *   header      — { title, icon_type, subtitle } from funnel instance
 *   mechanism   — mechanism name for context
 *   onExpand    — optional callback when user clicks the compact CTA
 */

import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, TrendingUp, Heart, Target, Home, CheckCircle,
  Activity, Search, Calendar, ChevronDown, ChevronUp,
} from "lucide-react";
import { resolveThemeTokens } from "@/lib/brandTokens";

// Icon map for header (matches ConversationFunnel HEADER_ICON_MAP subset)
const ICON_MAP = {
  shield: Shield,
  "trending-up": TrendingUp,
  heart: Heart,
  target: Target,
  home: Home,
  "check-circle": CheckCircle,
  activity: Activity,
  search: Search,
  calendar: Calendar,
};

/**
 * Maps builder_data.theme → brandConfig compatible with resolveThemeTokens
 */
function themeToBrandConfig(theme) {
  if (!theme || !theme.colors) return { accent: "#dc2626", surfaceMode: "dark" };
  return {
    accent: theme.colors.cta || "#dc2626",
    surfaceMode: "dark", // Profit Path builder is always dark
  };
}

export function CompactFunnelPreview({
  steps = [],
  theme = {},
  header = {},
  mechanism = "",
  onExpand,
  lpTokens = null,
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Resolve visual tokens from shared theme (same system as ConversationFunnel)
  const brandConfig = useMemo(() => themeToBrandConfig(theme), [theme]);
  const tokens = useMemo(() => resolveThemeTokens(brandConfig), [brandConfig]);

  // MA-LP-THEME-036: When lpTokens provided, derive accent from LP system
  const colors = theme?.colors || {};
  const ctaColor = lpTokens?.accent || colors.cta || tokens.accent;
  const ctaHover = colors.cta_hover || tokens.accentStrong;
  const ctaText = colors.cta_text || "#ffffff";
  const surface = colors.surface || tokens.surface1;
  const background = colors.background || tokens.pageBg;
  const text = colors.text || tokens.textPrimary;
  const textMuted = colors.text_muted || tokens.textMuted;
  const border = colors.border || tokens.headerBorder;
  const borderRadius = theme?.border_radius === "lg" ? "12px" : theme?.border_radius === "sm" ? "6px" : "8px";

  if (!steps || steps.length < 5) return null;

  const step1 = steps[0];
  const IconComp = ICON_MAP[header?.icon_type] || Target;

  const hookQuestion = step1?.headline || "Ready to get started?";

  // Derive compact CTA label
  const ctaLabel = (() => {
    const last = steps[4];
    if (last?.cta_label && last.cta_label.length < 30) return last.cta_label;
    return "Yes, show me";
  })();

  const handleExpand = () => {
    setIsExpanded(true);
    onExpand?.();
  };

  return (
    <div
      className="w-full overflow-hidden"
      style={{
        borderRadius,
        border: `1px solid ${border}`,
        background: surface,
      }}
      data-testid="compact-funnel-preview"
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3"
        style={{
          borderBottom: `1px solid ${border}`,
          background: background,
        }}
        data-testid="compact-funnel-header"
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: `${ctaColor}20` }}
        >
          <IconComp className="w-4 h-4" style={{ color: ctaColor }} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate" style={{ color: text }}>
            {header?.title || "Quick Assessment"}
          </p>
          {header?.subtitle && (
            <p className="text-[11px] truncate" style={{ color: textMuted }}>
              {header.subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Compact Body — Step 1 question + single CTA */}
      <div className="px-4 py-4">
        {/* Agent message bubble */}
        <div
          className="rounded-lg px-3 py-2.5 mb-3 max-w-[85%]"
          style={{
            background: `${surface}`,
            border: `1px solid ${border}`,
          }}
          data-testid="compact-funnel-message"
        >
          <p className="text-sm leading-relaxed" style={{ color: text }}>
            {hookQuestion}
          </p>
        </div>

        {/* Single CTA button — compact entry */}
        {!isExpanded && (
          <motion.button
            className="w-full py-2.5 px-4 text-sm font-medium transition-all cursor-pointer"
            style={{
              background: ctaColor,
              color: ctaText,
              borderRadius,
              border: "none",
            }}
            whileHover={{ scale: 1.01, background: ctaHover }}
            whileTap={{ scale: 0.98 }}
            onClick={handleExpand}
            data-testid="compact-funnel-cta"
          >
            {ctaLabel}
          </motion.button>
        )}
      </div>

      {/* Expanded State — shows remaining steps as preview */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden"
            data-testid="compact-funnel-expanded"
          >
            <div className="px-4 pb-4 space-y-3" style={{ borderTop: `1px solid ${border}` }}>
              <div className="pt-3">
                <p className="text-[10px] uppercase font-medium tracking-wider mb-2" style={{ color: textMuted }}>
                  Conversation Flow Preview
                </p>
              </div>
              {steps.slice(1).map((step, i) => (
                <div
                  key={step.id || i}
                  className="flex items-start gap-2 py-1.5"
                >
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-[10px] font-bold"
                    style={{
                      background: i === steps.length - 2 ? ctaColor : `${ctaColor}15`,
                      color: i === steps.length - 2 ? ctaText : ctaColor,
                    }}
                  >
                    {i + 2}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium" style={{ color: text }}>
                      {step.title || step.type}
                    </p>
                    <p className="text-[11px] line-clamp-2" style={{ color: textMuted }}>
                      {step.headline}
                    </p>
                    {step.type === "cta" && step.cta_label && (
                      <span
                        className="inline-block mt-1 px-2 py-0.5 text-[10px] font-medium rounded"
                        style={{ background: `${ctaColor}15`, color: ctaColor }}
                      >
                        {step.cta_label}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Collapse toggle */}
            <button
              className="w-full py-2 flex items-center justify-center gap-1 text-[11px] transition-colors cursor-pointer"
              style={{ color: textMuted, borderTop: `1px solid ${border}` }}
              onClick={() => setIsExpanded(false)}
              data-testid="compact-funnel-collapse"
            >
              <ChevronUp className="w-3 h-3" />
              Collapse preview
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default CompactFunnelPreview;
